import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS } from "../constants/frameworks";
import { motion } from "framer-motion";
import {
  FileDown, FileText, Shield, AlertTriangle, Bug, ClipboardCheck,
  Wrench, BarChart3, Download, CheckSquare, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { StatusBadge, TEXT_TONE } from "../components/ui/status-badge";
import { getScoreTone } from "../lib/statusTokens";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const Reports = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const { activeFrameworks } = useOrg();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedSections, setSelectedSections] = useState({
    compliance: true, risks: true, incidents: true,
    audits: true, cas: true, documents: true
  });

  useEffect(() => {
    fetchSummary();
  }, [token]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/reports/executive-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const sections = Object.entries(selectedSections)
        .filter(([, v]) => v).map(([k]) => k).join(",");
      const response = await axios.get(`${API}/reports/export-pdf?sections=${sections || 'all'}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ComplianceOS_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("rep.export_success"));
    } catch (error) {
      toast.error(t("rep.export_failed"));
    } finally {
      setExporting(false);
    }
  };

  const toggleSection = (key) => {
    setSelectedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t("rep.loading")}</p>
        </div>
      </div>
    );
  }

  const c = summary?.compliance || {};
  const fwScores = c.framework_scores || {};

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      <PageHeader
        title={t("reports.title")}
        description={t("reports.subtitle")}
        actions={
          <Button onClick={exportPDF} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? t("rep.generating") : t("reports.export_pdf")}
          </Button>
        }
      />

      {/* Executive Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Compliance Overview */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="lg:col-span-2">
          <Card className="border-border" data-testid="exec-compliance-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {t("rep.compliance_overview")}
              </CardTitle>
              <CardDescription>{t("rep.compliance_overview_desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-sm text-muted-foreground">{t("rep.overall_score")}</p>
                  <p className={`text-4xl font-heading font-bold ${TEXT_TONE[getScoreTone(c.overall_score || 0)]}`}>
                    {c.overall_score || 0}%
                  </p>
                </div>
                <Separator orientation="vertical" className="h-16" />
                <div>
                  <p className="text-sm text-muted-foreground">{t("rep.health_score")}</p>
                  <div className="flex items-center gap-2">
                    <p className={`text-4xl font-heading font-bold ${TEXT_TONE[getScoreTone(c.health_score || 0)]}`}>
                      {c.health_score || 0}
                    </p>
                    <StatusBadge kind="grade" value={c.health_grade || "N/A"} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {FRAMEWORKS
                  .filter(fw => activeFrameworks.includes(fw.id))
                  .map(fw => ({ name: fw.short, key: fw.id, color: fw.color }))
                  .map(fw => (
                  <div key={fw.key} className="p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: fw.color }} />
                      <span className="text-xs font-medium">{fw.name}</span>
                    </div>
                    <p className={`text-2xl font-heading font-bold ${TEXT_TONE[getScoreTone(fwScores[fw.key] || 0)]}`}>
                      {fwScores[fw.key] || 0}%
                    </p>
                    <Progress value={fwScores[fw.key] || 0} className="h-1.5 mt-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right: PDF Export Options */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Card className="border-border" data-testid="export-options-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileDown className="w-5 h-5" />
                {t("reports.sections")}
              </CardTitle>
              <CardDescription>{t("reports.sections_desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "compliance", labelKey: "reports.compliance_scores", icon: Shield },
                { key: "risks", labelKey: "reports.risk_summary", icon: AlertTriangle },
                { key: "incidents", labelKey: "reports.incident_summary", icon: Bug },
                { key: "audits", labelKey: "reports.audit_summary", icon: ClipboardCheck },
                { key: "cas", labelKey: "reports.ca_summary", icon: Wrench },
                { key: "documents", labelKey: "reports.doc_summary", icon: FileText }
              ].map(section => (
                <div key={section.key} className="flex items-center gap-3">
                  <Checkbox
                    id={`section-${section.key}`}
                    checked={selectedSections[section.key]}
                    onCheckedChange={() => toggleSection(section.key)}
                    data-testid={`section-checkbox-${section.key}`}
                  />
                  <Label htmlFor={`section-${section.key}`} className="flex items-center gap-2 cursor-pointer text-sm">
                    <section.icon className="w-4 h-4 text-muted-foreground" />
                    {t(section.labelKey)}
                  </Label>
                </div>
              ))}
              <Separator className="my-2" />
              <Button onClick={exportPDF} disabled={exporting} className="w-full" size="sm" data-testid="export-pdf-btn-sidebar">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {exporting ? t("rep.generating") : t("rep.download_pdf")}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
          <Card className="border-border" data-testid="risks-summary-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("nav.risks")}</p>
                  <p className="text-3xl font-heading font-bold mt-1">{summary?.risks?.total || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
              <div className="flex items-center gap-3 mt-3 text-sm">
                <span className="text-red-500">{summary?.risks?.critical || 0} {t("critical")}</span>
                <span className="text-orange-500">{summary?.risks?.high || 0} {t("high")}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3 }}>
          <Card className="border-border" data-testid="incidents-summary-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("nav.incidents")}</p>
                  <p className="text-3xl font-heading font-bold mt-1">{summary?.incidents?.total || 0}</p>
                </div>
                <Bug className="w-8 h-8 text-red-500 opacity-50" />
              </div>
              <p className="text-sm mt-3"><span className="text-yellow-500">{summary?.incidents?.open || 0} {t("open")}</span></p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <Card className="border-border" data-testid="cas-summary-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("nav.corrective_actions")}</p>
                  <p className="text-3xl font-heading font-bold mt-1">{summary?.corrective_actions?.total || 0}</p>
                </div>
                <Wrench className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
              <p className="text-sm mt-3"><span className="text-yellow-500">{summary?.corrective_actions?.open || 0} {t("open")}</span></p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <Card className="border-border" data-testid="docs-summary-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("nav.documents")}</p>
                  <p className="text-3xl font-heading font-bold mt-1">{summary?.documents?.total || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
              <p className="text-sm mt-3"><span className="text-green-500">{summary?.documents?.published || 0} {t("docs.published")}</span></p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Report Info */}
      <Card className="border-border">
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground text-center">
            {t("rep.generated_at")} {summary?.generated_at ? new Date(summary.generated_at).toLocaleString() : "N/A"}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
